import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Download, Mail, MailCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useTableTools } from "@/lib/useTableTools";
import { exportCsv } from "@/lib/exportCsv";
import { BulkBar } from "@/components/BulkBar";
import { money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PartnerFields } from "@/components/PartnerFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, CheckSquare, Square } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const emptyPartner = { company_name: "", active: true, payment_status: "pending", total_earned: 0, total_paid: 0, total_pending: 0 };

export default function Partners() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPartner);
  const [editing, setEditing] = useState(null);

  const tools = useTableTools(rows, ["company_name", "vat_number", "tax_code", "phone", "email", "city", "country", "iban"]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/partners"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cleanObj = (obj) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => { out[k] = v === "" ? null : v; });
    out.company_name = obj.company_name || "Azienda";
    return out;
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/partners/${editing.id}`, cleanObj(form));
      else await api.post("/partners", cleanObj(form));
      toast.success("Salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`${t("common.delete")}?`)) return;
    try { await api.delete(`/partners/${p.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invite = async (p) => {
    if (!p.email) { toast.error("Il partner non ha un'email"); return; }
    try {
      const { data } = await api.post(`/admin/partners/${p.id}/invite`, {});
      toast.success(data.email_sent ? "Invito inviato" : "Account creato (email non inviata)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmEmail = async (p) => {
    try { await api.post(`/admin/partners/${p.id}/confirm-email`, {}); toast.success("Email confermata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const accountBadge = (p) => {
    if ((p.status || "").toLowerCase() === "suspended" || p.active === false)
      return { label: "Sospeso", cls: "bg-rose-50 text-rose-700 border-rose-200" };
    if (p.email_confirmed) return { label: "Confermato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (p.invite_status === "invited") return { label: "Non confermato", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    return { label: "In attesa", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  };

  const exportPayments = async () => {
    try {
      const res = await api.get("/partners/export/payments", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "partner_payments.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const BULK_COLUMNS = [
    { key: "company_name", label: "Azienda" }, { key: "vat_number", label: "P.IVA" },
    { key: "country", label: "Paese" }, { key: "iban", label: "IBAN" },
    { key: "payment_status", label: "Stato pagamento" }, { key: "total_pending", label: "Da pagare" },
  ];
  const onBulk = async (action, value) => {
    const ids = [...tools.selected];
    if (action === "export") { exportCsv(tools.selectedRows, BULK_COLUMNS, "partners.csv"); return; }
    if (action === "delete" && !window.confirm(`${t("common.delete")} ${ids.length}?`)) return;
    try {
      const { data } = await api.post("/partners/bulk", { ids, action, value });
      toast.success(`✓ ${data.deleted ?? data.updated ?? 0}`);
      tools.clear(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.partners")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("sub.partners")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPayments} data-testid="partners-export-btn"><Download size={16} className="mr-1.5" /> Export pagamenti</Button>
          <Button className="bg-blue-900 hover:bg-blue-800" data-testid="partners-create-btn" onClick={() => { setForm(emptyPartner); setEditing(null); setOpen(true); }}><Plus size={16} className="mr-1.5" /> {t("common.create")}</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={tools.query} onChange={(e) => tools.setQuery(e.target.value)} placeholder={t("ph.partners")} className="pl-9" data-testid="partners-search" />
        </div>
        <Button variant="outline" size="sm" onClick={tools.selectAllVisible} data-testid="partners-select-all"><CheckSquare size={15} className="mr-1.5" /> Seleziona tutti</Button>
        <Button variant="outline" size="sm" onClick={tools.clear} data-testid="partners-clear"><Square size={15} className="mr-1.5" /> Deseleziona</Button>
        <span className="text-sm text-slate-400 ml-auto">{tools.filtered.length} / {rows.length}</span>
      </div>

      {tools.selected.size > 0 && (
        <BulkBar
          count={tools.selected.size}
          role={user?.role}
          statusOptions={[{ value: "active", label: "status.active" }, { value: "inactive", label: "status.inactive" }, { value: "suspended", label: "status.suspended" }]}
          actions={[
            { key: "export", kind: "export", action: "export", label: "bulk.export", roles: ["admin", "support"] },
            { key: "status", kind: "status", label: "bulk.status", roles: ["admin", "support"] },
            { key: "verified", kind: "verified", action: "set_verified", value: true, label: "bulk.verified", roles: ["admin"] },
            { key: "suspend", kind: "suspend", action: "suspend", label: "bulk.suspend", roles: ["admin", "support"] },
            { key: "delete", kind: "delete", action: "delete", label: "bulk.delete", roles: ["admin"] },
          ]}
          onAction={onBulk}
          onClear={tools.clear}
        />
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10"><Checkbox checked={tools.allVisibleSelected} onCheckedChange={(v) => v ? tools.selectAllVisible() : tools.clear()} data-testid="partners-header-checkbox" /></TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("col.company")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("col.contact")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("fld.country")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">IBAN</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("col.toPay")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("order.payment")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">Account</TableHead>
            <TableHead className="text-xs uppercase text-slate-500 text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">{t("common.loading")}</TableCell></TableRow>
              : tools.filtered.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
              : tools.filtered.map((p) => (
                <TableRow key={p.id} data-testid="partners-row">
                  <TableCell><Checkbox checked={tools.selected.has(p.id)} onCheckedChange={() => tools.toggle(p.id)} data-testid="partners-row-checkbox" /></TableCell>
                  <TableCell className="font-medium text-slate-800">{p.company_name}</TableCell>
                  <TableCell>{p.contact_name || "—"}</TableCell>
                  <TableCell>{p.country || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{p.iban || "—"}</TableCell>
                  <TableCell className="font-medium">{money(p.total_pending)}</TableCell>
                  <TableCell><StatusBadge status={p.payment_status || "pending"} /></TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${accountBadge(p).cls}`} data-testid="partner-account-badge">{accountBadge(p).label}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title={p.invite_status === "invited" ? "Reinvia invito" : "Invita partner"} onClick={() => invite(p)} data-testid="partner-invite-btn">
                        {p.invite_status === "invited" ? <Mail size={15} className="text-blue-700" /> : <Mail size={15} />}
                      </Button>
                      {!p.email_confirmed && (
                        <Button size="icon" variant="ghost" title="Segna email confermata" onClick={() => confirmEmail(p)} className="text-emerald-600" data-testid="partner-confirm-btn"><MailCheck size={15} /></Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/partners/${p.id}`)} data-testid="partner-view-btn"><Eye size={15} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => { setForm(p); setEditing(p); setOpen(true); }} data-testid="partners-edit-btn"><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-red-600" data-testid="partners-delete-btn"><Trash2 size={15} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? t("partner.edit") : t("partner.new")}</DialogTitle></DialogHeader>
          <PartnerFields form={form} setForm={setForm} adminMode={true} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="partners-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
